/**
 * Notification Item Component
 *
 * Displays a single notification in the dropdown list.
 * Features:
 * - Priority-based icon colors
 * - Unread indicator
 * - Click to navigate and mark as read
 * - Relative timestamp display
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortal } from '../../contexts/PortalContext';
import type { Notification } from '../../types/notification';
import {
  NotificationType,
  NotificationPriority,
  getNotificationIcon,
  formatNotificationTime,
} from '../../types/notification';
import './NotificationItem.css';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onClose: () => void;
}

/**
 * Get CSS class for icon based on notification priority
 */
function getIconColorClass(priority: NotificationPriority): string {
  switch (priority) {
    case NotificationPriority.HIGH:
      return 'notification-item__icon--high';
    case NotificationPriority.NORMAL:
      return 'notification-item__icon--normal';
    case NotificationPriority.LOW:
    default:
      return 'notification-item__icon--low';
  }
}

/**
 * Render notification type icon as SVG
 */
function NotificationIcon({ type }: { type: NotificationType }) {
  switch (type) {
    case NotificationType.QUESTION_RECEIVED:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" />
        </svg>
      );
    case NotificationType.QUESTION_ANSWERED:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 6h-2v9H6v2c0 .55.45 1 1 1h11l4 4V7c0-.55-.45-1-1-1zm-4 6V3c0-.55-.45-1-1-1H3c-.55 0-1 .45-1 1v14l4-4h10c.55 0 1-.45 1-1z" />
        </svg>
      );
    case NotificationType.TICKET_RESOLVED:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      );
    case NotificationType.ESCALATION:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
        </svg>
      );
    case NotificationType.NEW_REPLY:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" />
        </svg>
      );
    case NotificationType.TICKET_REOPENED:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
        </svg>
      );
    case NotificationType.QUEUE_ALERT:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
        </svg>
      );
    case NotificationType.ASSIGNMENT_CHANGE:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      );
    case NotificationType.STATUS_CHANGE:
    case NotificationType.TICKET_UPDATE:
    default:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 10.12h-6.78l2.74-2.82c-2.73-2.7-7.15-2.8-9.88-.1-2.73 2.71-2.73 7.08 0 9.79 2.73 2.71 7.15 2.71 9.88 0C18.32 15.65 19 14.08 19 12.1h2c0 1.98-.88 4.55-2.64 6.29-3.51 3.48-9.21 3.48-12.72 0-3.5-3.47-3.53-9.11-.02-12.58 3.51-3.47 9.14-3.47 12.65 0L21 3v7.12zM12.5 8v4.25l3.5 2.08-.72 1.21L11 13V8h1.5z" />
        </svg>
      );
  }
}

export function NotificationItem({ notification, onMarkAsRead, onClose }: NotificationItemProps) {
  const navigate = useNavigate();
  const { activePortal } = usePortal();

  const handleClick = useCallback(() => {
    // Mark as read
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }

    // Navigate to ticket
    const basePath = activePortal === 'it' ? '/ticketing' : '/user';
    navigate(`${basePath}/ticket/${notification.ticketId}`);

    // Close dropdown
    onClose();
  }, [notification, onMarkAsRead, navigate, activePortal, onClose]);

  const iconColorClass = getIconColorClass(notification.priority);

  return (
    <button
      className={`notification-item ${!notification.isRead ? 'notification-item--unread' : ''}`}
      onClick={handleClick}
      type="button"
    >
      {/* Unread indicator */}
      {!notification.isRead && <span className="notification-item__unread-dot" />}

      {/* Icon */}
      <span className={`notification-item__icon ${iconColorClass}`}>
        <NotificationIcon type={notification.type} />
      </span>

      {/* Content */}
      <div className="notification-item__content">
        <div className="notification-item__header">
          <span className="notification-item__ticket">{notification.ticketNumber}</span>
          <span className="notification-item__time">
            {formatNotificationTime(new Date(notification.createdAt))}
          </span>
        </div>
        <div className="notification-item__title">{notification.title}</div>
        <div className="notification-item__message">{notification.message}</div>
        <div className="notification-item__actor">
          <span className="notification-item__actor-name">{notification.actorName}</span>
        </div>
      </div>
    </button>
  );
}
